/**
 * Timing strip — visualises queued → started → completed durations as a
 * single proportional bar so operators can see whether time was spent
 * waiting for a worker or executing.
 */
import type { Task } from '@optum/api-client/types';

import { absoluteDateTime } from '../../lib/format.js';

export function TimingStrip({ task }: { task: Task }) {
  const created = Date.parse(task.created_at);
  const started = task.started_at ? Date.parse(task.started_at) : null;
  const completed = task.completed_at ? Date.parse(task.completed_at) : null;

  const queueMs = started !== null ? Math.max(0, started - created) : null;
  const runMs = completed !== null && started !== null ? Math.max(0, completed - started) : null;
  const totalMs = (queueMs ?? 0) + (runMs ?? 0);
  const queuePct = totalMs > 0 ? ((queueMs ?? 0) / totalMs) * 100 : 0;
  const runPct = 100 - queuePct;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      <div
        style={{
          display: 'flex',
          height: 8,
          overflow: 'hidden',
          borderRadius: 'var(--radius-chip)',
          background: 'var(--surface-sunken)',
        }}
      >
        {queueMs !== null && (
          <div
            title={`Queued ${fmt(queueMs)}`}
            style={{
              height: '100%',
              background: 'var(--surface-fill)',
              ...{ width: `${queuePct}%` },
            }}
          />
        )}
        {runMs !== null && (
          <div
            title={`Running ${fmt(runMs)}`}
            style={{ height: '100%', background: 'var(--fill-action)', ...{ width: `${runPct}%` } }}
          />
        )}
      </div>
      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 11,
          fontSize: 'var(--text-meta-lg)',
        }}
      >
        <Row label="Queued" stamp={task.created_at} duration={queueMs} />
        <Row label="Started" stamp={task.started_at} duration={runMs} />
        <Row label="Finished" stamp={task.completed_at} duration={null} />
      </dl>
    </div>
  );
}

function Row({
  label,
  stamp,
  duration,
}: {
  label: string;
  stamp: string | null;
  duration: number | null;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <dt style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>{label}</dt>
      <dd className="mono">{stamp ? absoluteDateTime(stamp) : '—'}</dd>
      {duration !== null && (
        <dd style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
          {fmt(duration)}
        </dd>
      )}
    </div>
  );
}

function fmt(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(2)} s`;
  const m = Math.floor(ms / 60_000);
  const s = ((ms % 60_000) / 1000).toFixed(0);
  return `${m}m ${s}s`;
}
